import { ReactNode, useCallback, useMemo } from 'react';
import { Checkbox, OverflowMenu, OverflowMenuItem, Tag } from '@carbon/react';
import type { Row } from '@tanstack/react-table';
import { format0 } from 'utils/number';
import type { DataTableColumnDef } from 'components/data-table';
import type { AddressGroup, AddressVariation, Run } from '../types';
import { executionColumns } from '../shared';
import {
  AddressGroupVariations,
  EXECUTABLE_STATUSES,
  REVERSIBLE_STATUSES,
} from './address-group-variations';
import { AddressStatusTag, MatchedOnTag } from './address-tags';
import { EntityCell, isToggleable } from './address-run-helpers';
import styles from '../page.module.scss';

// The group table's columns. Captured handlers read row data or stable
// setters only, so the memoized columns stay correct while keeping the table
// from re-initializing.
export const useAddressGroupColumns = ({
  groups,
  pendingIds,
  pendingMemberIds,
  selectionLocked,
  reverseBlocked,
  onToggle,
  onReverse,
}: {
  groups: AddressGroup[];
  pendingIds: Set<number>;
  pendingMemberIds: Set<number>;
  selectionLocked: boolean;
  reverseBlocked: boolean;
  onToggle: (ids: number[], included: boolean) => void;
  onReverse: (group: AddressGroup) => void;
}): DataTableColumnDef<AddressGroup>[] =>
  useMemo<DataTableColumnDef<AddressGroup>[]>(() => {
    const busy = (g: AddressGroup) =>
      pendingIds.has(g.id) ||
      g.variations.some((v) =>
        v.memberIds.some((m) => pendingMemberIds.has(m))
      );
    const toggleable = groups.filter(isToggleable);
    const includedCount = toggleable.filter((g) =>
      EXECUTABLE_STATUSES.has(g.status)
    ).length;
    const allIncluded =
      toggleable.length > 0 && includedCount === toggleable.length;
    return [
      {
        id: 'included',
        header: () => (
          <div className={styles.includeCell}>
            <Checkbox
              id="include-page"
              labelText="Include every group on this page"
              hideLabel
              checked={allIncluded}
              indeterminate={includedCount > 0 && !allIncluded}
              disabled={
                selectionLocked ||
                toggleable.length === 0 ||
                toggleable.some(busy)
              }
              onChange={() => {
                // As Carbon's select-all: include all unless all already are.
                const changing = allIncluded
                  ? toggleable
                  : toggleable.filter(
                      (g) => !EXECUTABLE_STATUSES.has(g.status)
                    );
                onToggle(
                  changing.map((g) => g.id),
                  !allIncluded
                );
              }}
            />
          </div>
        ),
        size: 48,
        enableSorting: false,
        cell: ({ row }) => {
          const group = row.original;
          return (
            <div className={styles.includeCell}>
              <Checkbox
                id={`include-${group.id}`}
                labelText={`Include ${group.normalizedName}`}
                hideLabel
                checked={EXECUTABLE_STATUSES.has(group.status)}
                disabled={
                  !isToggleable(group) || selectionLocked || busy(group)
                }
                onChange={(_e, { checked }) => onToggle([group.id], checked)}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 170,
        meta: { wrap: true, align: 'left' },
        cell: ({ row }) => (
          <div>
            <AddressStatusTag status={row.original.status} />
            {row.original.exclusionReason && (
              <p className={styles.addressLine}>
                {row.original.exclusionReason}
              </p>
            )}
          </div>
        ),
      },
      {
        id: 'entity',
        header: 'Entity',
        size: 260,
        meta: { wrap: true, align: 'left' },
        cell: ({ row }) => (
          <EntityCell
            id={row.original.entityId}
            name={row.original.entityName}
            kind={row.original.entityKind}
          />
        ),
      },
      {
        id: 'address',
        header: 'Address',
        size: 360,
        meta: { wrap: true, align: 'left' },
        cell: ({ row }) =>
          `std: ${
            row.original.standardizedForm ?? row.original.normalizedName
          }`,
      },
      {
        id: 'primary',
        header: 'Primary',
        size: 120,
        cell: ({ row }) => {
          if (!row.original.hasPrimary) return null;
          return (
            <Tag type="blue" size="sm">
              primary
            </Tag>
          );
        },
      },
      {
        accessorKey: 'matchedOn',
        header: 'Matched on',
        size: 150,
        cell: ({ row }) => <MatchedOnTag value={row.original.matchedOn} />,
      },
      {
        accessorKey: 'variationCount',
        header: 'Variations',
        size: 110,
        cell: ({ getValue }) => format0(getValue() as number),
      },
      {
        accessorKey: 'docs',
        header: 'Docs',
        size: 100,
        cell: ({ getValue }) => format0(getValue() as number),
      },
      {
        id: 'actions',
        header: '',
        size: 64,
        meta: { overflowVisible: true },
        cell: ({ row }) => {
          const group = row.original;
          if (!REVERSIBLE_STATUSES.has(group.status)) return null;
          return (
            <OverflowMenu
              size="sm"
              flipped
              aria-label={`Actions for ${group.normalizedName}`}
              iconDescription={`Actions for ${group.normalizedName}`}
            >
              <OverflowMenuItem
                itemText="Reverse group"
                isDelete
                disabled={reverseBlocked}
                onClick={() => onReverse(group)}
              />
            </OverflowMenu>
          );
        },
      },
    ];
  }, [
    groups,
    pendingIds,
    pendingMemberIds,
    reverseBlocked,
    selectionLocked,
    onToggle,
    onReverse,
  ]);

// The Executions table's columns for an address run.
export const useAddressExecutionColumns = ({
  reverseBlocked,
  onReverse,
}: {
  reverseBlocked: boolean;
  onReverse: (run: Run) => void;
}): DataTableColumnDef<Run>[] =>
  useMemo(
    () =>
      executionColumns({
        appliedHeader: 'Deduplicated',
        bulkType: 'addresses',
        reverseBlocked,
        onReverse,
      }),
    [reverseBlocked, onReverse]
  );

// Every table/row builder the run page's tables need: the group columns, the
// expandable variations sub-row, and the executions columns.
export const useAddressRunColumns = ({
  groups,
  pendingIds,
  pendingMemberIds,
  selectionLocked,
  reverseBlocked,
  toggleGroups,
  toggleVariation,
  onReverseGroup,
  onReverseVariation,
  onReverseRun,
}: {
  groups: AddressGroup[];
  pendingIds: Set<number>;
  pendingMemberIds: Set<number>;
  selectionLocked: boolean;
  reverseBlocked: boolean;
  toggleGroups: (ids: number[], included: boolean) => void;
  toggleVariation: (memberIds: number[], included: boolean) => void;
  onReverseGroup: (group: AddressGroup) => void;
  onReverseVariation: (variation: AddressVariation) => void;
  onReverseRun: (run: Run) => void;
}): {
  columns: DataTableColumnDef<AddressGroup>[];
  execColumns: DataTableColumnDef<Run>[];
  renderVariations: (row: Row<AddressGroup>) => ReactNode;
} => {
  const columns = useAddressGroupColumns({
    groups,
    pendingIds,
    pendingMemberIds,
    selectionLocked,
    reverseBlocked,
    onToggle: toggleGroups,
    onReverse: onReverseGroup,
  });
  const execColumns = useAddressExecutionColumns({
    reverseBlocked,
    onReverse: onReverseRun,
  });
  const renderVariations = useCallback(
    (row: Row<AddressGroup>) => (
      <AddressGroupVariations
        group={row.original}
        selectionLocked={selectionLocked}
        reverseBlocked={reverseBlocked}
        groupPending={pendingIds.has(row.original.id)}
        pendingIds={pendingMemberIds}
        onToggle={toggleVariation}
        onReverse={onReverseVariation}
      />
    ),
    [
      pendingIds,
      pendingMemberIds,
      reverseBlocked,
      selectionLocked,
      toggleVariation,
      onReverseVariation,
    ]
  );
  return { columns, execColumns, renderVariations };
};
